import { useState } from 'react';

interface UseScheduledQueueFlowOptions<Post> {
  /** Whether the composer holds a draft that opening a post would discard. */
  hasDraft: () => boolean;
  /** Opens the composer on the post. */
  onEdit: (post: Post) => void;
  /** Resolves with the post under its new author, and rejects once the host reported why. */
  takeOver: (post: Post) => Promise<Post>;
}

// What the team's queue opens over itself: the picker that replaces it to reschedule a post, the confirmation of a
// deletion, the one that discards the composer's draft before a post opens in it, and the one an admin agrees to before
// taking over a post. The host renders them and sends each action.
export const useScheduledQueueFlow = <Post>({ hasDraft, onEdit, takeOver }: UseScheduledQueueFlowOptions<Post>) => {
  const [reschedulingPost, setReschedulingPost] = useState<Post>();
  const [deletingPost, setDeletingPost] = useState<Post>();
  const [pendingEditPost, setPendingEditPost] = useState<Post>();
  const [takeoverPost, setTakeoverPost] = useState<Post>();
  const [isTakingOver, setIsTakingOver] = useState(false);

  const openPost = (post: Post) => (hasDraft() ? setPendingEditPost(post) : onEdit(post));

  return {
    queueActions: {
      onEdit: openPost,
      onReschedule: setReschedulingPost,
      onTakeOver: setTakeoverPost,
      onDelete: setDeletingPost,
    },
    reschedulingPost,
    closeReschedule: () => setReschedulingPost(undefined),
    deletingPost,
    closeDelete: () => setDeletingPost(undefined),
    /** The post that opens once the member agrees to discard the draft. */
    pendingEditPost,
    confirmDiscard: () => {
      if (!pendingEditPost) return;
      setPendingEditPost(undefined);
      onEdit(pendingEditPost);
    },
    closeDiscard: () => setPendingEditPost(undefined),
    takeoverPost,
    confirmTakeover: async () => {
      if (!takeoverPost || isTakingOver) return;
      setIsTakingOver(true);
      const takenOverPost = await takeOver(takeoverPost).catch(() => undefined);
      setIsTakingOver(false);
      setTakeoverPost(undefined);
      if (takenOverPost) openPost(takenOverPost);
    },
    // The confirmation stays until the takeover answers, which then opens the post or reports why it can't.
    closeTakeover: () => {
      if (!isTakingOver) setTakeoverPost(undefined);
    },
  };
};
